import React, { useMemo } from 'react';
import { Issue } from '../../data/mockData';
import { Badge } from '../ui/Badge';
import { CheckCircle2, Circle, ExternalLink, Bug, BookOpen, Zap, Layers } from 'lucide-react';
import { GitHubPR, GitHubCommit, GitHubBranch } from '../../services/githubData';
import { buildGitLinkMap } from '../../services/issueGitLinker';
import { GitLinkedBadges } from './GitLinkedBadges';
interface IssueListProps {
  issues: Issue[];
  limit?: number;
  jiraBaseUrl?: string;
  showJiraLink?: boolean;
  compact?: boolean;
  githubPrs?: GitHubPR[];
  githubCommits?: GitHubCommit[];
  githubBranches?: GitHubBranch[];
}
export function IssueList({
  issues,
  limit,
  jiraBaseUrl = 'https://acme-corp.atlassian.net/browse',
  showJiraLink = true,
  compact = false,
  githubPrs = [],
  githubCommits = [],
  githubBranches = []
}: IssueListProps) {
  const safeIssues = Array.isArray(issues) ? issues : [];
  const displayIssues = limit ? safeIssues.slice(0, limit) : safeIssues;
  // Build git link map only when GitHub data is available
  const gitLinkMap = useMemo(() => {
    if (githubPrs.length === 0 && githubCommits.length === 0 && githubBranches.length === 0) {
      return {};
    }
    const keys = displayIssues.map(i => i.key);
    return buildGitLinkMap(keys, githubPrs, githubCommits, githubBranches);
  }, [displayIssues, githubPrs, githubCommits, githubBranches]);
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Done':
        return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" data-id="element-2397" />;
      case 'In Progress':
        return <Circle className="w-4 h-4 text-blue-400 fill-blue-900/30 shrink-0" data-id="element-2398" />;
      case 'Review':
        return <Circle className="w-4 h-4 text-amber-400 fill-amber-900/30 shrink-0" data-id="element-2399" />;
      case 'Backlog':
        return <Circle className="w-4 h-4 text-slate-600 shrink-0" data-id="element-2400" />;
      default:
        return <Circle className="w-4 h-4 text-slate-600 shrink-0" data-id="element-2401" />;
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Bug':
        return <Bug className="w-3.5 h-3.5 text-red-400 shrink-0" data-id="element-2402" />;
      case 'Story':
        return <BookOpen className="w-3.5 h-3.5 text-emerald-400 shrink-0" data-id="element-2403" />;
      case 'Task':
        return <Zap className="w-3.5 h-3.5 text-blue-400 shrink-0" data-id="element-2404" />;
      case 'Epic':
        return <Layers className="w-3.5 h-3.5 text-violet-400 shrink-0" data-id="element-2405" />;
      default:
        return <Circle className="w-3.5 h-3.5 text-slate-500 shrink-0" data-id="element-2406" />;
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'text-red-400 bg-red-950/50 border border-red-900/50';
      case 'Medium':
        return 'text-amber-400 bg-amber-950/50 border border-amber-900/50';
      case 'Low':
        return 'text-blue-400 bg-blue-950/50 border border-blue-900/50';
      default:
        return 'text-slate-400 bg-slate-900 border border-slate-800';
    }
  };
  return <div className={compact ? 'space-y-1.5' : 'space-y-2'} data-id="element-2407">
      {displayIssues.map(issue => {
      const linked = gitLinkMap[issue.key];
      return <div key={issue.key} className={`flex items-center justify-between rounded-lg border border-slate-800 hover:border-slate-600 transition-colors group ${compact ? 'p-2 gap-2 bg-slate-800/30' : 'p-3 gap-3 bg-slate-800/50'}`} data-id="element-2408">
            <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-2409">
              {getStatusIcon(issue.status)}
              <div className="flex-1 min-w-0" data-id="element-2410">
                <div className="flex items-center gap-1.5" data-id="element-2411">
                  {getTypeIcon(issue.type)}
                  <p className={`font-medium text-slate-200 group-hover:text-indigo-400 transition-colors truncate ${compact ? 'text-xs' : 'text-sm'}`} data-id="element-2412">
                    {issue.summary}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5" data-id="element-2413">
                  <span className={`text-slate-500 font-mono ${compact ? 'text-[10px]' : 'text-xs'}`} data-id="element-2414">
                    {issue.key}
                  </span>
                  <span className="text-xs text-slate-600" data-id="element-2415">·</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getPriorityColor(issue.priority)}`} data-id="element-2416">
                    {issue.priority}
                  </span>
                  {!compact && issue.labels && issue.labels.slice(0, 2).map(label => <span key={label} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-900 text-slate-500 border border-slate-800" data-id="element-2417">
                        {label}
                      </span>)}
                  {linked && <GitLinkedBadges linked={linked} compact={compact} data-id="element-2418" />}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0" data-id="element-2419">
              {issue.assignee ? <div className={`rounded-full bg-slate-700 flex items-center justify-center font-bold text-slate-300 ${compact ? 'w-5 h-5 text-[8px]' : 'w-6 h-6 text-[10px]'}`} title={issue.assignee.name} data-id="element-2420">
                  {issue.assignee.initials}
                </div> : !compact ? <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 border-dashed flex items-center justify-center text-[10px] text-slate-600" title="Sin asignar" data-id="element-2421">
                  ?
                </div> : null}
              {issue.points ? <Badge variant="outline" className={`bg-slate-900/50 ${compact ? 'text-[9px] px-1 py-0' : ''}`} data-id="element-2422">
                  {issue.points}p
                </Badge> : !compact ? <Badge variant="outline" className="bg-slate-900/50 text-slate-600" data-id="element-2423">
                  —
                </Badge> : null}
              {showJiraLink && !compact && <a href={`${jiraBaseUrl}/${issue.key}`} target="_blank" rel="noopener noreferrer" className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md hover:bg-slate-700 text-slate-500 hover:text-indigo-400" title={`Ver ${issue.key} en Jira`} onClick={e => e.stopPropagation()} data-id="element-2424">
                  <ExternalLink size={14} data-id="element-2425" />
                </a>}
            </div>
          </div>;
    })}
    </div>;
}